(() => {
  const DAY = 86400000;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  const store = {
    get(area, key) {
      try {
        return window[area].getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(area, key, value) {
      try {
        window[area].setItem(key, value);
      } catch (e) {
        /* storage unavailable — fine */
      }
    },
    remove(area, key) {
      try {
        window[area].removeItem(key);
      } catch (e) {
        /* ignore */
      }
    },
  };

  // Accepts "2026-10-06", "2026-10-06 21:15" or full ISO strings
  const parseDate = (value) => {
    if (!value) return null;
    const v = value.trim();
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T00:00:00` : v.replace(' ', 'T');
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  /* ---------------------------------------------------------------- */
  /* Pop-up                                                            */
  /* ---------------------------------------------------------------- */
  const popup = document.querySelector('[data-ciao-popup]');
  const popupIsOpen = () => Boolean(popup && popup.open);

  if (popup && typeof popup.showModal === 'function') {
    const key = popup.dataset.key;
    const days = Number(popup.dataset.days) || 14;
    const sectionEl = popup.closest('.shopify-section');

    const remember = (ms) => store.set('localStorage', key, String(Date.now() + ms));
    const open = () => {
      if (popupIsOpen()) return;
      popup.showModal();
    };
    const close = () => {
      if (popup.open) popup.close();
    };

    popup.addEventListener('close', () => {
      if (!designMode) remember(days * DAY);
    });
    popup.addEventListener('click', (event) => {
      if (event.target === popup) close();
    });
    popup.querySelectorAll('[data-ciao-popup-close]').forEach((btn) => btn.addEventListener('click', close));

    const form = popup.closest('form');
    if (form) {
      form.addEventListener('submit', () => {
        store.set('sessionStorage', 'ciao-popup-submitted', '1');
        remember(365 * DAY);
      });
    }

    if (designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        if (sectionEl && event.target === sectionEl) open();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (sectionEl && event.target === sectionEl) close();
      });
    } else if (popup.hasAttribute('data-open-now') && store.get('sessionStorage', 'ciao-popup-submitted')) {
      store.remove('sessionStorage', 'ciao-popup-submitted');
      open();
    } else if (!popup.hasAttribute('data-open-now')) {
      const until = Number(store.get('localStorage', key)) || 0;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const allowed = Date.now() > until && !(isMobile && popup.dataset.mobile === 'false');

      if (allowed) {
        const trigger = popup.dataset.trigger;
        let fired = false;
        const fire = () => {
          if (fired) return;
          fired = true;
          window.removeEventListener('scroll', onScroll);
          open();
        };
        const onScroll = () => {
          const max = document.documentElement.scrollHeight - window.innerHeight;
          if (max > 0 && (window.scrollY / max) * 100 >= Number(popup.dataset.scroll)) fire();
        };
        if (trigger !== 'scroll') setTimeout(fire, (Number(popup.dataset.delay) || 25) * 1000);
        if (trigger !== 'time') window.addEventListener('scroll', onScroll, { passive: true });
      }
    }
  }

  /* ---------------------------------------------------------------- */
  /* Recent purchase notes                                             */
  /* ---------------------------------------------------------------- */
  const toastRoot = document.querySelector('[data-ciao-toast]');

  if (toastRoot && !store.get('sessionStorage', 'ciao-toast-off')) {
    const template = toastRoot.querySelector('[data-ciao-toast-items]');
    const slot = toastRoot.querySelector('.ciao-toast-slot');
    const maxAge = (Number(toastRoot.dataset.maxAge) || 14) * DAY;
    const maxShows = Number(toastRoot.dataset.maxShows) || 3;
    const firstDelay = (Number(toastRoot.dataset.firstDelay) || 14) * 1000;
    const now = Date.now();
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

    const ago = (date) => {
      const mins = Math.round((now - date.getTime()) / 60000);
      if (mins < 60) return rtf.format(-Math.max(mins, 1), 'minute');
      const hours = Math.round(mins / 60);
      if (hours < 24) return rtf.format(-hours, 'hour');
      return rtf.format(-Math.round(hours / 24), 'day');
    };

    // Real entries only: valid date, not in the future, not older than max age
    const items = [...template.content.querySelectorAll('.ciao-toast')].filter((el) => {
      const date = parseDate(el.dataset.at);
      if (!date) return false;
      const age = now - date.getTime();
      if (age < -3600000 || age > maxAge) return false;
      el.querySelector('[data-ago]').textContent = ago(date);
      return true;
    });

    // Shuffle so the order differs each visit
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }

    let shown = 0;
    let hideTimer;
    let nextTimer;
    let stopped = false;

    const hide = (done) => {
      const current = slot.firstElementChild;
      toastRoot.classList.remove('has-toast');
      if (!current) return done && done();
      current.classList.remove('is-shown');
      setTimeout(() => {
        current.remove();
        if (done) done();
      }, reduceMotion ? 0 : 600);
    };

    const scheduleNext = () => {
      if (stopped || shown >= Math.min(maxShows, items.length)) return;
      const wait = 22000 + Math.random() * 20000;
      nextTimer = setTimeout(show, wait);
    };

    const show = () => {
      if (stopped) return;
      if (popupIsOpen() || document.hidden) {
        nextTimer = setTimeout(show, 8000);
        return;
      }
      const el = items[shown].cloneNode(true);
      shown += 1;
      toastRoot.hidden = false;
      slot.replaceChildren(el);
      requestAnimationFrame(() => {
        el.classList.add('is-shown');
        toastRoot.classList.add('has-toast');
      });

      const startHide = () => {
        hideTimer = setTimeout(() => hide(scheduleNext), 7000);
      };
      el.addEventListener('mouseenter', () => clearTimeout(hideTimer));
      el.addEventListener('mouseleave', startHide);
      el.addEventListener('focus', () => clearTimeout(hideTimer));
      startHide();
    };

    toastRoot.querySelector('[data-ciao-toast-close]').addEventListener('click', () => {
      stopped = true;
      clearTimeout(hideTimer);
      clearTimeout(nextTimer);
      store.set('sessionStorage', 'ciao-toast-off', '1');
      hide();
    });

    if (items.length) nextTimer = setTimeout(show, designMode ? 1500 : firstDelay);
  }

  /* ---------------------------------------------------------------- */
  /* In arrivo — shipment                                              */
  /* ---------------------------------------------------------------- */
  document.querySelectorAll('[data-ciao-ship]').forEach((ship) => {
    let departed = parseDate(ship.dataset.departed);
    let eta = parseDate(ship.dataset.eta);
    const today = new Date();

    if ((!departed || !eta) && designMode) {
      // Preview only, so the layout can be seen in the theme editor
      departed = new Date(today.getTime() - 19 * DAY);
      eta = new Date(today.getTime() + 22 * DAY);
    }
    if (!departed || !eta || eta <= departed) return;

    const route = ship.querySelector('[data-route]');
    const done = ship.querySelector('[data-route-done]');
    const vessel = ship.querySelector('[data-vessel]');
    const length = route.getTotalLength();
    const total = eta - departed;
    const progress = Math.min(1, Math.max(0, (today - departed) / total));

    const fmt = new Intl.DateTimeFormat('en-NZ', { day: 'numeric', month: 'short' });
    const depEl = ship.querySelector('[data-date="departed"]');
    const etaEl = ship.querySelector('[data-date="eta"]');
    if (depEl) depEl.textContent = fmt.format(departed);
    if (etaEl) etaEl.textContent = fmt.format(eta);

    const daysEl = ship.querySelector('[data-days]');
    const daysLabel = ship.querySelector('[data-days-label]');
    const label = ship.querySelector('[data-progress-label]');
    const bar = ship.querySelector('[data-progress-bar]');
    const totalDays = Math.round(total / DAY);

    if (today < departed) {
      daysEl.textContent = Math.ceil((departed - today) / DAY);
      daysLabel.textContent = 'days until we set sail';
      label.textContent = 'Loading cargo';
    } else if (today >= eta) {
      daysEl.textContent = '✓';
      daysLabel.textContent = 'landed — unpacking now';
      label.textContent = 'Voyage complete';
    } else {
      const left = Math.ceil((eta - today) / DAY);
      daysEl.textContent = left;
      daysLabel.textContent = left === 1 ? 'day until we dock' : 'days until we dock';
      const dayOf = Math.min(totalDays, Math.floor((today - departed) / DAY) + 1);
      label.textContent = `Day ${dayOf} of ${totalDays} · ${Math.round(progress * 100)}% of the voyage`;
    }

    done.style.strokeDasharray = `${length} ${length}`;
    done.style.strokeDashoffset = `${length}`;

    const place = (p) => {
      const at = length * p;
      const pt = route.getPointAtLength(at);
      const ahead = route.getPointAtLength(Math.min(length, at + 2));
      const behind = route.getPointAtLength(Math.max(0, at - 2));
      const dx = ahead.x - behind.x;
      const dy = ahead.y - behind.y;
      let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
      const flip = dx < 0;
      if (flip) angle -= 180;
      angle = Math.max(-28, Math.min(28, angle));
      vessel.style.transform = `translate(${pt.x}px, ${pt.y}px) rotate(${angle}deg) scale(${flip ? -1 : 1}, 1)`;
      done.style.strokeDashoffset = `${length * (1 - p)}`;
      if (bar) bar.style.width = `${p * 100}%`;
    };

    place(0);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          ship.classList.toggle('is-visible', entry.isIntersecting);
          if (entry.isIntersecting && !ship.dataset.sailed) {
            ship.dataset.sailed = '1';
            if (reduceMotion) {
              place(progress);
              return;
            }
            // Sail from the origin to today's position along the route
            const start = performance.now();
            const duration = 2400;
            const step = (time) => {
              const t = Math.min(1, (time - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              place(progress * eased);
              if (t < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.25 }
    );
    observer.observe(ship);
  });
})();
